import { inject, Injectable } from '@angular/core';
import { IActivityPlatformApiService } from './activity-platform-api.interface';
import { map, Observable } from 'rxjs';
import {
  ActivityPlatform,
  ActivityPlatformHistory,
  ActivityPlatformLite,
} from '../domain/activity-platform.model';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ResponseApi } from '@domain/shared/infrastructure/models/response-api.interface';
import {
  mapActivitiesPlatformLite,
  mapActivityPlatform,
  mapActivityPlatformLogDetail,
  mapAllActivitiesPlatformHistory,
  mapAllActivityPlatforms,
} from '../mappers/activity-platform.mapper';
import {
  ActivityPlatformApiModel,
  ActivityPlatformLiteApiModel,
} from './activity-platform-api.model';
import { ActivityPlatformLogData } from '../domain/activity-platform-log-data.model';
import { ActivityPlatformLogDataApiModel } from './activity-platform-log-data-api.model';

@Injectable({
  providedIn: 'root',
})
export class ActivityPlatformApiService implements IActivityPlatformApiService {
  private readonly _baseUrl = environment.baseUrl;
  private readonly _http = inject(HttpClient);

  constructor() {}

  getActivityPlatforms(queryParams?: {
    id: number;
    uuid: string;
    fol: string;
    limit: number;
    kind: string;
    isFinished: boolean;
  }): Observable<ActivityPlatform[] | ActivityPlatform> {
    const { id, uuid, fol, limit, kind, isFinished } = queryParams || {};
    const url = `${this._baseUrl}activity-platform?id=${id}&uuid=${
      uuid || ''
    }&fol=${fol || ''}&limit=${limit}&kind=${kind || ''}&isFinished=${
      isFinished || false
    }`;
    const headers = new HttpHeaders();
    return this._http.get<ResponseApi>(url, { headers }).pipe(
      map((response) => {
        if (Array.isArray(response.results)) {
          return mapAllActivityPlatforms(
            response.results as ActivityPlatformApiModel[]
          );
        }
        return mapActivityPlatform(
          response.results as ActivityPlatformApiModel
        );
      })
    );
  }

  getActivityPlatformsPaginate(queryParams?: {
    page: number;
    pageSize?: number;
    isFinished?: boolean;
    kind?: string;
    fol?: string;
    withRelations?: boolean;
    uuid?: string;
  }): Observable<{
    data: ActivityPlatformHistory[] | ActivityPlatformLite[];
    total: number;
  }> {
    const { page, pageSize, isFinished, kind, fol, withRelations, uuid } =
      queryParams || {};
    const url = `${this._baseUrl}activity-platforms?page=${
      page || 1
    }&pageSize=${pageSize || 10}&isFinished=${isFinished || false}&kind=${
      kind || ''
    }&fol=${fol || ''}&withRelations=${withRelations || false}&uuid=${
      uuid || ''
    }`;
    const headers = new HttpHeaders();
    return this._http.get<ResponseApi>(url, { headers }).pipe(
      map((response) => {
        if (!withRelations) {
          return {
            data: mapActivitiesPlatformLite(
              response.results.data as ActivityPlatformLiteApiModel[]
            ),
            total: response.results.total,
          };
        }

        return {
          data: mapAllActivitiesPlatformHistory(
            response.results.data as ActivityPlatformApiModel[]
          ),
          total: response.results.total,
        };
      })
    );
  }

  getActivityLogDataById(id: number): Observable<ActivityPlatformLogData> {
    const url = `${this._baseUrl}activity-platform/logs/${id}`;
    const headers = new HttpHeaders();
    return this._http.get<ResponseApi>(url, { headers }).pipe(
      map((response) => {
        return mapActivityPlatformLogDetail(
          response.results as ActivityPlatformLogDataApiModel
        );
      })
    );
  }

  createActivityPlatform(body: {
    typeActivityPlatformId: number;
    shippingNoticeId: number;
  }): Observable<ActivityPlatform> {
    const url = `${this._baseUrl}activity-platform`;
    const headers = new HttpHeaders();
    return this._http.post<ResponseApi>(url, body, { headers }).pipe(
      map((response) => {
        return mapActivityPlatform(
          response.results as ActivityPlatformApiModel
        );
      })
    );
  }

  updateActivityPlatform(body: {
    activityPlatformId: number;
  }): Observable<ActivityPlatform> {
    const url = `${this._baseUrl}activity-platform`;
    const headers = new HttpHeaders();
    return this._http.put<ResponseApi>(url, body, { headers }).pipe(
      map((response) => {
        return mapActivityPlatform(
          response.results as ActivityPlatformApiModel
        );
      })
    );
  }

  finishActivityPlatform(id: number): Observable<ActivityPlatform> {
    const url = `${this._baseUrl}activity-platform/${id}/finish`;
    const headers = new HttpHeaders();
    return this._http.put<ResponseApi>(url, {}, { headers }).pipe(
      map((response) => {
        return mapActivityPlatform(
          response.results as ActivityPlatformApiModel
        );
      })
    );
  }

  deleteActivityPlatform(id: number): Observable<boolean> {
    const url = `${this._baseUrl}activity-platform/${id}`;
    const headers = new HttpHeaders();
    return this._http.delete<ResponseApi>(url, { headers }).pipe(
      map((response) => {
        return response.ok;
      })
    );
  }
}
